import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Label,
  Modal,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@noor/ui';
import { toast, type ModalRenderProps } from '@noor/lib';
import { Can } from '@/entities/access';
import { closeChatFormSchema } from '../model/schema';
import { useCloseChat } from '../model/use-chat-mutations';

/**
 * Close a chat (cpanel "Введите параметры закрытия"): pick resolved / not
 * resolved and give a reason. Both are required. Like cpanel, only the status
 * is sent — the reason is a client-side guard the backend has no field for.
 */
export function CloseChatModal({ payload, close }: ModalRenderProps<'close-chat'>) {
  const { t } = useTranslation('inbox');
  const [status, setStatus] = useState('');
  const [reason, setReason] = useState('');
  const mutation = useCloseChat(payload.chatId);

  const onSubmit = () => {
    const parsed = closeChatFormSchema.safeParse({ status: Number(status), reason });
    if (!parsed.success) {
      toast.error(t('close.allFields'));
      return;
    }
    mutation.mutate(parsed.data.status, { onSuccess: close });
  };

  return (
    <Can permission="inbox.close">
      <Modal
        title={t('close.title')}
        dismissible={!mutation.isPending}
        footer={
          <>
            <Button
              variant="soft"
              size="modal"
              shape="rounded"
              onClick={close}
              disabled={mutation.isPending}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              variant="dark"
              size="modal"
              shape="rounded"
              onClick={onSubmit}
              disabled={mutation.isPending}
            >
              {t('close.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="close-status" className="px-[var(--form-label-padding-x)]">
              {t('close.status')}
            </Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger id="close-status" className="w-full">
                <SelectValue placeholder={t('close.statusPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3">{t('close.resolved')}</SelectItem>
                <SelectItem value="4">{t('close.unresolved')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="close-reason" className="px-[var(--form-label-padding-x)]">
              {t('close.reason')}
            </Label>
            <Textarea
              id="close-reason"
              rows={4}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
        </div>
      </Modal>
    </Can>
  );
}
